import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import { IconQrcode } from "@tabler/icons-react";
import { useIntl } from "react-intl";
import ModalComponent from "components/Modal";
import type { FilaInventario, QrProducto } from "../types/catalogoInventario.types";
import { COLORES_INVENTARIO } from "../constants/catalogoInventario.constants";
import { fecha, moneda, textoOVacio } from "../utils/catalogoInventarioFormato.utils";
import { useQrProducto } from "../hooks/useQrProducto";
import { QrProductoTarjeta } from "./QrProductoTarjeta";

interface Props {
    producto: FilaInventario | null;
    idUsuario: number;
    onCerrar: () => void;
    onError: (error: unknown) => void;
    onQrGenerado: (qr: QrProducto) => void;
}

const Dato = ({ etiqueta, valor, md = 4 }: { etiqueta: string; valor: string | number; md?: number }) => (
    <Grid item xs={6} md={md}>
        <Typography variant="body2" color="text.secondary">
            {etiqueta}
        </Typography>
        <Typography variant="body2" fontWeight="medium">
            {valor}
        </Typography>
    </Grid>
);

export const DetalleProductoModal = ({
    producto,
    idUsuario,
    onCerrar,
    onError,
    onQrGenerado,
}: Props): JSX.Element => {
    const intl = useIntl();
    const { qr, generando, generar } = useQrProducto(producto?.qr ?? null, {
        idUsuario,
        onError,
        onGenerado: onQrGenerado,
    });

    // Un solo QR por producto
    const accionGenerar = {
        title: intl.formatMessage({ id: generando ? "general_generando_qr" : "general_generar_qr" }),
        icon: <IconQrcode stroke={2} />,
        color: COLORES_INVENTARIO.qr,
        hoverColor: COLORES_INVENTARIO.qrHover,
        textColor: COLORES_INVENTARIO.qrTexto,
        disabledButton: generando || !producto,
        handleAction: () => {
            if (producto) generar(producto.id);
        },
    };

    return (
        <ModalComponent
            handleClose={onCerrar}
            isOpen={producto !== null}
            key="modal-detalle-producto"
            procesando={generando}
            textoHeader={producto?.nombre ?? ""}
            {...(!qr ? { actions: [accionGenerar] } : {})}
        >
            {producto && (
                <>
                    <Box sx={{ px: 2, pb: 2, mt: 3 }}>
                        <Paper elevation={2} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: COLORES_INVENTARIO.titulo }}>
                                Información del Producto
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Dato etiqueta="Nombre del producto" valor={producto.nombre} md={12} />
                                <Dato etiqueta="Clave" valor={textoOVacio(producto.clave)} />
                                <Dato etiqueta="Precio" valor={moneda(producto.precio)} />
                                <Dato etiqueta="Categoría" valor={textoOVacio(producto.categoria)} />
                                <Dato etiqueta="Ubicación" valor={textoOVacio(producto.ubicacion)} />
                                <Dato etiqueta="Almacén" valor={textoOVacio(producto.almacen)} />
                                <Dato etiqueta="Unidad de medida" valor={textoOVacio(producto.unidadMedida)} />
                                <Dato etiqueta="Tipo producto" valor={textoOVacio(producto.tipoProducto)} />
                                <Dato etiqueta="Cantidad" valor={producto.cantidad} />
                                <Dato etiqueta="Máximos" valor={producto.maximos} />
                                <Dato etiqueta="Mínimos" valor={producto.minimos} />
                                <Dato etiqueta="Punto de reorden" valor={producto.puntoReorden} />
                                <Dato etiqueta="Fecha de creación" valor={fecha(producto.fechaCreacion)} />
                                <Dato etiqueta="Clave QR" valor={qr?.clave ?? "Sin QR"} />
                            </Grid>
                        </Paper>
                    </Box>
                    {qr && <QrProductoTarjeta qr={qr} />}
                </>
            )}
        </ModalComponent>
    );
};
